import { Button, Dropdown, HStack, Icon, Spacer } from '@sgme/ui';
import { clsx } from 'clsx';
import { isValid, parseISO } from 'date-fns';
import { type CSSProperties, Fragment, type ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { toMultipassLegId } from '@/api/option/utils.ts';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useQueuedDispatch } from '@/App/tiles/Option/QueueContext.tsx';
import { FieldEdit } from '@/App/tiles/Option/strategy/FieldEdit.tsx';
import { FieldHeader } from '@/App/tiles/Option/strategy/FieldHeader.tsx';
import { StrategyPanelHeader } from '@/App/tiles/Option/strategy/StrategyPanelHeader.tsx';
import type { StrategyField } from '@/App/tiles/Option/strategy/types.tsx';
import { isDefined } from '@/components/sg-design-system/utils/index.ts';
import { FormattedFXDate } from '@/components/ui/FormattedFXDate.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { computeHedgeDisplayRules, getQuote, selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';

import './StrategyPanel.css';
import type { LegId, OptionProductName } from '@/store/option/state.ts';
import {
  addOptionLegThunk,
  changeOptionTypeThunk,
  duplicateOptionLegThunk,
  removeOptionLegThunk,
  resetOptionThunk,
} from '@/store/option/thunks/thunk.ts';

interface StrategyPanelProps {
  tileId: string;
}

export function StrategyPanel(props: StrategyPanelProps) {
  const { tileId } = props;
  const {
    currentUser: { userType },
  } = useUserContext();

  const {
    legs,
    hedgeDisplay,
    strategyPanelMode,
    quote,
    groupedByExpiry,
    productName,
    isBarrier2Present,
    shouldShowDatesinBarrier1,
    shouldShowDatesinBarrier2,
  } = useOptionSelector((option) => {
    const quote = getQuote(option);
    return {
      quote,
      legs: option.properties.legs ?? [],
      hedges: option.properties.hedges,
      hedgeType: option.properties.hedgeType,
      strategyPanelMode: option.properties.uiState.strategyPanelMode,
      groupedByExpiry: option.properties.uiState.groupLegsByExpiry,
      productName: option.type,
      hedgeDisplay: computeHedgeDisplayRules(option),
      shouldShowDatesinBarrier1: option.properties.legs.some((leg) => leg.barrier1?.nature === 'WINDOW'),
      shouldShowDatesinBarrier2: option.properties.legs.some((leg) => leg.barrier2?.nature === 'WINDOW'),
      isBarrier2Present: option.properties.legs.some((leg) => isDefined(leg.barrier2)),
    };
  });
  const isInternalSales = userType === 'Internal Sales';

  const queuedDispatch = useQueuedDispatch();

  const groups = Map.groupBy(legs, (leg) => {
    if (!groupedByExpiry) {
      return '';
    }
    return leg.expiryDate ?? 'No expiry';
  });

  const [openGroups, setOpenGroups] = useState<boolean[]>(
    groups
      .entries()
      .map(() => true)
      .toArray(),
  );

  function toggleGroup(groupIndex: number) {
    setOpenGroups((prev) => {
      return groups
        .entries()
        .map((_, index) => {
          const previouslyOpen = prev[index] ?? false;
          if (groupIndex === index) {
            return !prev[index];
          }
          return previouslyOpen;
        })
        .toArray();
    });
  }

  const isTriggerOrBinary =
    productName === 'Trigger' ||
    productName === 'Binary' ||
    legs.some((leg) => leg.legProductName === 'Trigger' || leg.legProductName === 'Binary');
  const showBarrier1Dates = isTriggerOrBinary && shouldShowDatesinBarrier1;
  const showBarrier2Dates = isTriggerOrBinary && shouldShowDatesinBarrier2;

  const fields: (StrategyField | StrategyField[])[] = [
    'side',
    ...(productName !== 'Binary' ? ['type' as const] : []),
    'expiry',
    ...(productName !== 'Binary' ? ['strike' as const] : []),
    'notional',
    'delivery',
    ...(isTriggerOrBinary ? ['triggerType' as const, 'firstTrigger1' as const, 'secondTrigger1' as const] : []),
    ...(showBarrier1Dates ? ['TriggerStartDate1' as const, 'TriggerEndDate1' as const] : []),
    ...(isBarrier2Present ? ['firstTrigger2' as const, 'secondTrigger2' as const] : []),
    ...(showBarrier2Dates ? ['TriggerStartDate2' as const, 'TriggerEndDate2' as const] : []),
    [
      'premium',
      ...(hedgeDisplay.premiumDate === 'strategy' ? ['premiumDate' as const] : []),
      ...(isInternalSales ? ['markup' as const] : []),
      'volatility',
    ],
    [
      ...(hedgeDisplay.hedgeRate === 'strategy' ? ['hedgeRate' as const] : []),
      ...(hedgeDisplay.hedgeAmount === 'strategy' ? ['hedgeAmount' as const] : []),
      ...(hedgeDisplay.hedgeDate === 'strategy' ? ['hedgeDate' as const] : []),
    ],

    ...(isInternalSales ? ['atmVolatility' as const] : []),
    'forward',
    'swap',
    'zc1',
    'zc2',
    'delta',
    'vega',
    'gamma',
    'theta',
  ];

  const nonEmptyFields = fields.filter((field) => !(Array.isArray(field) && field.length === 0));

  async function addLeg() {
    await queuedDispatch(addOptionLegThunk({ tileId }));
  }

  function groupSpan(group: StrategyField[]) {
    return group.length + 1;
  }

  function wrapFieldsInGroups(className: string, renderer: (field: StrategyField) => ReactNode): ReactNode[] {
    if (strategyPanelMode === 'horizontal') {
      return nonEmptyFields.flat().map((field) => {
        return renderer(field);
      });
    }
    return nonEmptyFields.map((fieldOrGroup): ReactNode => {
      if (Array.isArray(fieldOrGroup)) {
        return (
          <div
            key={`group-${fieldOrGroup[0]}`}
            style={{ gridRowEnd: `span ${groupSpan(fieldOrGroup)}` }}
            className={className}
          >
            {fieldOrGroup.map((field) => renderer(field))}
          </div>
        );
      }
      return renderer(fieldOrGroup);
    });
  }

  function countFields() {
    if (strategyPanelMode === 'horizontal') {
      return nonEmptyFields.flat().length;
    }

    return nonEmptyFields.reduce((total, field) => {
      if (Array.isArray(field)) {
        return total + groupSpan(field);
      }

      return total + 1;
    }, 0);
  }

  const nbFields = countFields() + 1 + (groupedByExpiry && strategyPanelMode === 'vertical' ? 1 : 0);

  return (
    <div className="d-flex flex-column flex-grow-1 border-start bg-lvl2">
      <StrategyPanelHeader />
      <div className="strategy-container" style={{ '--nb-fields': nbFields } as CSSProperties}>
        <div className={clsx('strategy-grid', strategyPanelMode === 'vertical' ? 'vertical' : 'horizontal')}>
          {groupedByExpiry && strategyPanelMode === 'vertical' && <div className="grid-cell header">Groups</div>}

          <AddLegButton onClick={addLeg} visible={productName === 'Vanilla' || isTriggerOrBinary} />

          {wrapFieldsInGroups('fieldGroup header', (fieldName) => (
            <FieldHeader key={fieldName} field={fieldName} tileId={tileId} />
          ))}

          {groups
            .entries()
            .toArray()
            .map(([expiryDate, legs], groupIndex) => {
              const isGroupOpen = openGroups[groupIndex] || groups.size === 1;
              return (
                <Fragment key={`group-${expiryDate}`}>
                  {groupedByExpiry && (
                    <div
                      style={{ '--nb-items': legs.length } as CSSProperties}
                      className={`group-header ps-1 ${isGroupOpen ? 'open' : 'closed'}`}
                      onClick={() => toggleGroup(groupIndex)}
                    >
                      <FormatDateString date={expiryDate} /> ({legs.length} legs){' '}
                      <em className="icon icon-sm cursor-pointer">
                        {isGroupOpen ? 'keyboard_arrow_left' : 'keyboard_arrow_right'}
                      </em>
                    </div>
                  )}

                  {legs.map((leg) => {
                    const legId = leg.legId;

                    const mpLegId = toMultipassLegId(legId, productName);
                    const legData = quote?.legsData?.[mpLegId];
                    // const legData = selectOptionQuoteLegs(option, legId);
                    return (
                      <Fragment key={legId}>
                        <LegHeader
                          tileId={tileId}
                          legId={legId}
                          visible={isGroupOpen}
                          isTriggerOrBinary={isTriggerOrBinary}
                        />

                        {wrapFieldsInGroups('fieldGroup', (fieldName) => (
                          <FieldEdit
                            key={fieldName}
                            visible={isGroupOpen}
                            field={fieldName}
                            leg={leg}
                            quoteLeg={legData}
                            legId={legId}
                            tileId={tileId}
                          />
                        ))}
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function AddLegButton(props: { onClick: () => void; visible: boolean }) {
  return (
    <div className="grid-cell header">
      {props.visible && (
        <Button iconPosition="end" purpose="flat" color="secondary" onClick={props.onClick} data-e2e="addLeg-button">
          <b>
            <FormattedMessage id="option.actions.addLeg" />
          </b>
          <Icon icon="add" />
        </Button>
      )}
    </div>
  );
}

function LegHeader({
  tileId,
  visible,
  legId,
  isTriggerOrBinary,
}: {
  tileId: string;
  legId: LegId;
  visible: boolean;
  isTriggerOrBinary: boolean;
}) {
  const queuedDispatch = useQueuedDispatch();
  const {
    currentUser: {
      featureToggles: { optionBinary, optionTrigger },
    },
  } = useUserContext();

  const { currencyPair, legs, productName, uiState } = useOptionSelector((option) => ({
    legs: option.properties.legs ?? [],
    productName: option.type,
    currencyPair: selectOptionCurrency(option).currencyPair,
    uiState: option.properties.uiState,
  }));


  const isStrategyProduct = productName === 'Strangle' || productName === 'Straddle' || productName === 'RiskReversal';

  const options = (
    ['Vanilla', 'Trigger', 'Binary', 'Straddle', 'Strangle', 'RiskReversal'] as OptionProductName[]
  ).filter(
    (type) =>
      (type !== 'Trigger' || optionTrigger) &&
      (type !== 'Binary' || optionBinary) &&
      (isStrategyProduct || legs.length < 2 || (type !== 'Straddle' && type !== 'Strangle' && type !== 'RiskReversal')),
  );

  async function changeOptionType(changedProductName: OptionProductName) {
    const shouldResetOption =
      isStrategyProduct ||
      changedProductName === 'Strangle' ||
      changedProductName === 'Straddle' ||
      changedProductName === 'RiskReversal';

    if (!shouldResetOption) {
      await queuedDispatch(changeOptionTypeThunk({ tileId, legProductName: changedProductName, legId }));
      return;
    }

    await queuedDispatch(
      resetOptionThunk({
        tileId: tileId,
        newType: changedProductName,
        currencyPair,
        uiState,
      }),
    );
  }

  async function handleDuplicateLeg() {
    await queuedDispatch(duplicateOptionLegThunk({ tileId, legId }));
  }

  async function handleRemoveLeg() {
    await queuedDispatch(removeOptionLegThunk({ tileId, legId }));
  }

  return (
    <HStack className={clsx('grid-cell', { 'd-none': !visible })}>
      <Dropdown color={'flat-primary' as 'primary'} direction="auto" size="md">
        <Dropdown.Toggle data-e2e="strategy-leg-label">
          {productName === 'RiskReversal' || productName === 'Straddle' || productName === 'Strangle'
            ? productName
            : legs.find((leg) => leg.legId === legId)?.legProductName}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {options.map((productName) => (
            <Dropdown.Item
              key={productName}
              onClick={() => changeOptionType(productName)}
              data-e2e={`select-${productName}`}
            >
              <FormattedMessage id={`option.type.${productName}`} />
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {(productName === 'Vanilla' || isTriggerOrBinary) && (
        <>
          <Spacer />
          <Dropdown color={'flat-primary' as 'primary'} direction="auto" size="md">
            <Dropdown.Toggle className="btn-icon" data-e2e="legMenu-btn">
              <i className="icon">more_vert</i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleDuplicateLeg} data-e2e="duplicateLeg-menu-item">
                <FormattedMessage id="option.actions.duplicateLeg" />
              </Dropdown.Item>
              {legs.length > 1 && (
                <Dropdown.Item
                  onClick={handleRemoveLeg}
                  // state={canRemoveLeg ? undefined : 'disabled'}
                  data-e2e="removeLeg-menu-item"
                >
                  <FormattedMessage id="option.actions.removeLeg" />
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </>
      )}
    </HStack>
  );
}

function FormatDateString({ date }: { date: string }) {
  return <>{isValid(parseISO(date)) ? <FormattedFXDate value={date} dateStyle="medium" /> : 'No date'}</>;
}
